/**
 * TEAM INSIGHTS MODAL
 * 
 * AI-powered team research for survivor/pick'em contests with tabs:
 * - Stats: Team record, rankings, yards per game
 * - Intel: AI-powered matchup analysis, trends
 * - Community: Chat about the team
 */

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, Brain, MessageCircle, Users, Send, Loader,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Zap, Star, ThumbsUp, Info, Shield, Target, Trophy
} from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { API_BASE_URL } from '../constants'

interface Team {
  name: string
  abbreviation?: string
  record?: string
  opponent?: string
  isHome?: boolean
  gameDate?: string
  gameTime?: string
}

interface Game {
  homeTeam: string
  awayTeam: string
  gameDate?: string
  gameTime?: string
  venue?: string
}

interface TeamInsightsModalProps {
  isOpen: boolean
  onClose: () => void
  team: Team | null
  game: Game | null
  contestId: string | number
  userId?: number
  username?: string
  onPickTeam?: (teamName: string) => void
  isPicked?: boolean
}

interface TeamStats {
  name: string
  record?: string
  wins?: number
  losses?: number
  ties?: number
  divisionRank?: string
  conferenceRank?: string
  pointsFor?: number
  pointsAgainst?: number
  offensiveRank?: number
  defensiveRank?: number
  yardsPerGame?: number
  yardsAllowedPerGame?: number
  passingYardsPerGame?: number
  rushingYardsPerGame?: number
  recentGames?: Array<{ opponent: string; result: string; score: string }>
  streak?: string
  homeRecord?: string
  awayRecord?: string
}

interface Insights {
  summary?: string
  strengths?: string[]
  weaknesses?: string[]
  recentNews?: Array<{ headline: string; sentiment: string }>
  injuries?: string[]
  matchupAnalysis?: string
  predictionReasoning?: string
  confidenceLevel?: string
  recommendation?: string
  weatherImpact?: string
}

interface ChatMessage {
  id: number
  user_id: number
  username: string
  message: string
  message_type: string
  created_at: string
}

export default function TeamInsightsModal({
  isOpen,
  onClose,
  team,
  game,
  contestId,
  userId,
  username,
  onPickTeam,
  isPicked
}: TeamInsightsModalProps) {
  const [activeTab, setActiveTab] = useState<'stats' | 'intel' | 'chat'>('stats')
  
  // Stats tab state
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  
  // Intel tab state
  const [insights, setInsights] = useState<Insights | null>(null)
  const [hasInsights, setHasInsights] = useState(false)
  const [loadingInsights, setLoadingInsights] = useState(false)
  const [isResearching, setIsResearching] = useState(false)
  const [researchedBy, setResearchedBy] = useState<string | null>(null)
  
  // AI chat tab state
  const [aiQuestion, setAiQuestion] = useState('')
  const [aiConversation, setAiConversation] = useState<Array<{ role: 'user' | 'ai'; content: string }>>([])
  const [isAskingAI, setIsAskingAI] = useState(false)
  
  // Community chat tab state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loadingChat, setLoadingChat] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  
  const chatEndRef = useRef<HTMLDivElement>(null)
  
  // Load data when modal opens
  useEffect(() => {
    if (isOpen && team) {
      loadStats()
      loadInsights()
      loadChat()
    }
  }, [isOpen, team, contestId])
  
  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, aiConversation])
  
  // Load team stats
  const loadStats = async () => {
    if (!team) return
    
    try {
      setLoadingStats(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/team/${encodeURIComponent(team.name)}/stats`
      )
      
      if (response.data.success) {
        setTeamStats(response.data.team)
      }
    } catch (error) {
      console.error('Failed to load team stats:', error)
      // Show basic info even if API fails
      setTeamStats({
        name: team.name,
        record: team.record
      })
    } finally {
      setLoadingStats(false)
    }
  }
  
  // Load cached insights
  const loadInsights = async () => {
    if (!team) return
    
    try {
      setLoadingInsights(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/team/${encodeURIComponent(team.name)}/insights`
      )
      
      if (response.data.hasInsights) {
        setInsights(response.data.insights.insights_json)
        setHasInsights(true)
        setResearchedBy(response.data.insights.researched_by_username)
      } else {
        setInsights(null)
        setHasInsights(false)
      }
    } catch (error) {
      console.error('Failed to load insights:', error)
    } finally {
      setLoadingInsights(false)
    }
  }
  
  // Research team with AI
  const doResearch = async () => {
    if (!team || !userId) {
      toast.error('Please log in to research teams')
      return
    }
    
    try {
      setIsResearching(true)
      toast.loading('🔬 Researching team...', { id: 'research' })
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/team/${encodeURIComponent(team.name)}/research`,
        {
          userId,
          username,
          teamName: team.name,
          opponent: team.opponent || game?.homeTeam === team.name ? game?.awayTeam : game?.homeTeam,
          gameDate: game?.gameDate,
          isHome: team.isHome ?? (game?.homeTeam === team.name)
        }
      )
      
      if (response.data.success) {
        setInsights(response.data.insights.insights_json)
        setHasInsights(true)
        setResearchedBy(username || 'You')
        toast.success('Research complete!', { id: 'research' })
      }
    } catch (error: any) {
      console.error('Research failed:', error)
      toast.error('Research failed: ' + (error.response?.data?.error || error.message), { id: 'research' })
    } finally {
      setIsResearching(false)
    }
  }
  
  // Ask AI about the team
  const askAI = async () => {
    if (!aiQuestion.trim() || !team) return
    
    const question = aiQuestion
    setAiQuestion('')
    setAiConversation(prev => [...prev, { role: 'user', content: question }])
    
    try {
      setIsAskingAI(true)
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/team/${encodeURIComponent(team.name)}/ask-ai`,
        {
          question,
          teamName: team.name,
          opponent: team.opponent || (game?.homeTeam === team.name ? game?.awayTeam : game?.homeTeam),
          gameDate: game?.gameDate
        }
      )
      
      if (response.data.success) {
        setAiConversation(prev => [...prev, { role: 'ai', content: response.data.answer }])
      }
    } catch (error: any) {
      console.error('AI question failed:', error)
      setAiConversation(prev => [...prev, { role: 'ai', content: 'Sorry, I couldn\'t process that question. Please try again.' }])
    } finally {
      setIsAskingAI(false)
    }
  }
  
  // Load community chat
  const loadChat = async () => {
    if (!team) return
    
    try {
      setLoadingChat(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/team/${encodeURIComponent(team.name)}/chat`
      )
      setChatMessages(response.data.messages || [])
    } catch (error) {
      console.error('Failed to load chat:', error)
    } finally {
      setLoadingChat(false)
    }
  }
  
  // Send chat message
  const sendChatMessage = async () => {
    if (!newMessage.trim() || !team || !userId) return
    
    const message = newMessage
    setNewMessage('')
    
    try {
      setSendingMessage(true)
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/team/${encodeURIComponent(team.name)}/chat`,
        {
          userId,
          username,
          message,
          messageType: 'chat'
        }
      )
      
      if (response.data.success) {
        setChatMessages(prev => [...prev, response.data.message])
      }
    } catch (error: any) {
      console.error('Failed to send message:', error)
      toast.error('Failed to send message')
    } finally {
      setSendingMessage(false)
    }
  }
  
  if (!isOpen || !team) return null
  
  const opponent = team.opponent || (game?.homeTeam === team.name ? game?.awayTeam : game?.homeTeam)
  const isHome = team.isHome ?? (game?.homeTeam === team.name)
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="font-bold text-white text-lg">{team.name}</h2>
                  <p className="text-sm text-gray-400">
                    {isHome ? '🏠 Home' : '✈️ Away'} vs {opponent}
                    {game?.gameDate && ` • ${game.gameDate}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setActiveTab('stats')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'stats'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <Info className="w-4 h-4" />
                Stats
              </button>
              <button
                onClick={() => setActiveTab('intel')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'intel'
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <Brain className="w-4 h-4" />
                Intel & AI
                {hasInsights && <CheckCircle className="w-3 h-3 text-green-400" />}
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'chat'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Community
                {chatMessages.length > 0 && (
                  <span className="text-xs bg-slate-700 px-1.5 rounded">{chatMessages.length}</span>
                )}
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {/* STATS TAB */}
            {activeTab === 'stats' && (
              <div>
                {loadingStats ? (
                  <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
                    <p className="text-gray-400 mt-2">Loading team data...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Team Header */}
                    <div className="flex items-center gap-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-4">
                      <div className="w-20 h-20 bg-amber-500/20 rounded-xl flex items-center justify-center">
                        <Trophy className="w-10 h-10 text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-white">{team.name}</h2>
                        <p className="text-gray-400">{isHome ? 'Home' : 'Away'} vs {opponent}</p>
                        {teamStats?.record && (
                          <p className="text-emerald-400 font-semibold">{teamStats.record}</p>
                        )}
                      </div>
                      {teamStats?.streak && (
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${String(teamStats.streak).startsWith('W') ? 'text-emerald-400' : String(teamStats.streak).startsWith('L') ? 'text-red-400' : 'text-gray-400'}`}>
                            {String(teamStats.streak)}
                          </div>
                          <p className="text-xs text-gray-500">Streak</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Record & Rankings */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {teamStats?.record && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Record</p>
                          <p className="text-lg font-bold text-white">{teamStats.record}</p>
                        </div>
                      )}
                      {teamStats?.divisionRank && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Division</p>
                          <p className="text-lg font-bold text-white">{teamStats.divisionRank}</p>
                        </div>
                      )}
                      {teamStats?.homeRecord && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Home</p>
                          <p className="text-lg font-bold text-white">{teamStats.homeRecord}</p>
                        </div>
                      )}
                      {teamStats?.awayRecord && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Away</p>
                          <p className="text-lg font-bold text-white">{teamStats.awayRecord}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Offensive & Defensive Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Offense */}
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="w-5 h-5 text-emerald-400" />
                          <span className="font-semibold text-emerald-400">Offense</span>
                          {teamStats?.offensiveRank && (
                            <span className="ml-auto text-sm text-gray-400">#{teamStats.offensiveRank}</span>
                          )}
                        </div>
                        <div className="space-y-2">
                          {teamStats?.yardsPerGame && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Yards/Game</span>
                              <span className="text-white font-medium">{teamStats.yardsPerGame.toFixed(1)}</span>
                            </div>
                          )}
                          {teamStats?.passingYardsPerGame && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Pass Yds/Game</span>
                              <span className="text-white font-medium">{teamStats.passingYardsPerGame.toFixed(1)}</span>
                            </div>
                          )}
                          {teamStats?.rushingYardsPerGame && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Rush Yds/Game</span>
                              <span className="text-white font-medium">{teamStats.rushingYardsPerGame.toFixed(1)}</span>
                            </div>
                          )}
                          {teamStats?.pointsFor && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Pts/Game</span>
                              <span className="text-white font-medium">{(teamStats.pointsFor / ((teamStats.wins || 0) + (teamStats.losses || 0) || 1)).toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Defense */}
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-5 h-5 text-blue-400" />
                          <span className="font-semibold text-blue-400">Defense</span>
                          {teamStats?.defensiveRank && (
                            <span className="ml-auto text-sm text-gray-400">#{teamStats.defensiveRank}</span>
                          )}
                        </div>
                        <div className="space-y-2">
                          {teamStats?.yardsAllowedPerGame && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Yds Allowed/Game</span>
                              <span className="text-white font-medium">{teamStats.yardsAllowedPerGame.toFixed(1)}</span>
                            </div>
                          )}
                          {teamStats?.pointsAgainst && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Pts Allowed/Game</span>
                              <span className="text-white font-medium">{(teamStats.pointsAgainst / ((teamStats.wins || 0) + (teamStats.losses || 0) || 1)).toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Recent Games */}
                    {teamStats?.recentGames && teamStats.recentGames.length > 0 && (
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-emerald-400" />
                          Recent Games
                        </h3>
                        <div className="space-y-2">
                          {teamStats.recentGames.slice(0, 5).map((game, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm">
                              <span className="text-gray-400">{game.opponent}</span>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${game.result === 'W' ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {game.result}
                                </span>
                                <span className="text-white">{game.score}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* No stats available message */}
                    {!teamStats?.record && !teamStats?.yardsPerGame && (
                      <div className="text-center py-8 text-gray-400">
                        <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Team stats not available yet.</p>
                        <p className="text-sm">Try the Intel tab for AI analysis!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* INTEL TAB */}
            {activeTab === 'intel' && (
              <div className="space-y-4">
                {loadingInsights ? (
                  <div className="text-center py-8">
                    <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                    <p className="text-gray-400 mt-2">Loading insights...</p>
                  </div>
                ) : hasInsights && insights ? (
                  <div className="space-y-4">
                    {/* Researched By */}
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Brain className="w-4 h-4 text-blue-400" />
                      <span>Researched by <span className="text-blue-400 font-medium">{researchedBy}</span></span>
                    </div>
                    
                    {/* Summary */}
                    {insights.summary && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          AI Summary
                        </h3>
                        <p className="text-gray-300 text-sm leading-relaxed">{insights.summary}</p>
                      </div>
                    )}
                    
                    {/* Matchup Analysis */}
                    {insights.matchupAnalysis && (
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <h3 className="font-semibold text-white mb-2">Matchup Analysis</h3>
                        <p className="text-gray-300 text-sm leading-relaxed">{insights.matchupAnalysis}</p>
                      </div>
                    )}
                    
                    {/* Strengths & Weaknesses */}
                    <div className="grid grid-cols-2 gap-4">
                      {insights.strengths && insights.strengths.length > 0 && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                          <h4 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Strengths
                          </h4>
                          <ul className="space-y-1 text-sm text-gray-300">
                            {insights.strengths.map((s, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle className="w-3 h-3 text-emerald-400 mt-1 flex-shrink-0" />
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {insights.weaknesses && insights.weaknesses.length > 0 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                          <h4 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                            <TrendingDown className="w-4 h-4" />
                            Weaknesses
                          </h4>
                          <ul className="space-y-1 text-sm text-gray-300">
                            {insights.weaknesses.map((w, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <AlertTriangle className="w-3 h-3 text-red-400 mt-1 flex-shrink-0" />
                                <span>{w}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Injuries */}
                    {insights.injuries && insights.injuries.length > 0 && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                        <h4 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Key Injuries
                        </h4>
                        <ul className="space-y-1 text-sm text-gray-300">
                          {insights.injuries.map((injury, i) => (
                            <li key={i}>{injury}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Recommendation */}
                    {insights.recommendation && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                        <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                          <Star className="w-4 h-4" />
                          Recommendation
                        </h4>
                        <p className="text-gray-300 text-sm">{insights.recommendation}</p>
                        {insights.confidenceLevel && (
                          <p className="text-xs text-gray-500 mt-2">Confidence: {insights.confidenceLevel}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Research Again Button */}
                    <button
                      onClick={doResearch}
                      disabled={isResearching}
                      className="w-full py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isResearching ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4" />
                          Research Again
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 text-blue-400/50 mx-auto mb-4" />
                    <h3 className="font-semibold text-white mb-2">No Research Yet</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Get AI-powered insights on {team.name}'s matchup against {opponent}
                    </p>
                    <button
                      onClick={doResearch}
                      disabled={isResearching || !userId}
                      className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto"
                    >
                      {isResearching ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4" />
                          Research This Team
                        </>
                      )}
                    </button>
                    {!userId && (
                      <p className="text-xs text-gray-500 mt-2">Log in to research teams</p>
                    )}
                  </div>
                )}
                
                {/* AI Q&A Section */}
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    Ask AI About {team.name}
                  </h3>
                  
                  {/* AI Conversation */}
                  {aiConversation.length > 0 && (
                    <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                      {aiConversation.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-xl px-4 py-2 text-sm ${
                            msg.role === 'user' 
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-800 text-gray-300'
                          }`}>
                            {msg.content}
                          </div>
                        </div>
                      ))}
                      {isAskingAI && (
                        <div className="flex justify-start">
                          <div className="bg-slate-800 rounded-xl px-4 py-2">
                            <Loader className="w-4 h-4 animate-spin text-amber-400" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AI Input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={aiQuestion}
                      onChange={(e) => setAiQuestion(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && askAI()}
                      placeholder={`Ask about ${team.name}...`}
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                    />
                    <button
                      onClick={askAI}
                      disabled={!aiQuestion.trim() || isAskingAI}
                      className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* CHAT TAB */}
            {activeTab === 'chat' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-400" />
                  Community Discussion
                </h3>
                
                {loadingChat ? (
                  <div className="text-center py-8">
                    <Loader className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                  </div>
                ) : chatMessages.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className="bg-slate-800/50 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-amber-400 text-sm">{msg.username}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm">{msg.message}</p>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
                
                {/* Chat Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder={userId ? "Share your thoughts..." : "Log in to chat"}
                    disabled={!userId}
                    className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 disabled:opacity-50"
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={!newMessage.trim() || sendingMessage || !userId}
                    className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {sendingMessage ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer - Pick Button */}
          {onPickTeam && (
            <div className="p-4 border-t border-slate-700 bg-slate-800/50">
              <button
                onClick={() => {
                  onPickTeam(team.name)
                  onClose()
                }}
                disabled={isPicked}
                className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                  isPicked
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                }`}
              >
                {isPicked ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Already Picked
                  </>
                ) : (
                  <>
                    <Trophy className="w-5 h-5" />
                    Pick {team.name}
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

